import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "@/components/LangLink";
import { ArrowUpRight, ImageIcon, Star, X, RotateCcw } from "lucide-react";
import { PageHero, CTASection } from "@/components/sections";
import { Stagger, StaggerItem } from "@/components/primitives";
import { useContent } from "@/lib/content";
import { BENDAGGI_FAMILIES } from "@/lib/bendaggi";

// Sotto-filtri (secondo livello) per categoria principale.
const SUBFILTERS = {
  terapeutiche: [
    { id: "calze-indumenti", label: "Calze e indumenti compressivi" },
    { id: "sistemi-adattivi", label: "Sistemi compressivi adattivi" },
  ],
  preventive: [
    { id: "linea-70", label: "Linea 70" },
    { id: "linea-140", label: "Linea 140" },
    { id: "calzini-comfort", label: "Calzini Comfort" },
    { id: "gambaletto", label: "Gambaletto" },
    { id: "autoreggente", label: "Autoreggente" },
    { id: "monocollant", label: "Monocollant" },
    { id: "collant-gestante", label: "Collant gestante" },
    { id: "collant-calibrato", label: "Collant calibrato" },
  ],
  altri: [
    { id: "ortesi-tutori", label: "Ortesi e tutori" },
    { id: "accessori", label: "Accessori" },
  ],
};

const ProductCard = ({ p, photoLabel, brandLabel, cta }) => (
  <StaggerItem className="h-full">
    <Link
      to={p.to}
      data-testid={`catalog-card-${p.id}`}
      className={`group flex h-full flex-col overflow-hidden rounded-2xl border bg-white transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-green hover:shadow-[0_24px_50px_-18px_rgba(15,25,35,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green focus-visible:ring-offset-2 ${p.featured ? "border-green/40 ring-1 ring-green/20" : "border-[#E7EDF2]"}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-cloud">
        {p.img ? (
          <img src={p.img} alt={p.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-navy/30" data-testid={`catalog-photo-placeholder-${p.id}`}>
            <ImageIcon size={30} />
            <span className="text-[11px] font-semibold uppercase tracking-wider">{photoLabel}</span>
          </div>
        )}
        {p.featured && (
          <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-green px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-white shadow-sm" data-testid={`catalog-badge-${p.id}`}>
            <Star size={9} className="fill-white" /> Tra i più scelti
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-6">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-green-deep">{p.catLabel}</span>
        <h3 className="mt-1.5 font-serif text-xl font-bold text-navy">{p.name}</h3>
        {p.brand && <p className="mt-1 text-[11px] uppercase tracking-wider text-muted-foreground">{brandLabel}: {p.brand}</p>}
        <p className="mt-3 flex-1 text-sm text-muted-foreground leading-relaxed">{p.short}</p>
        <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-green transition-all group-hover:gap-3">
          {cta} <ArrowUpRight size={15} />
        </span>
      </div>
    </Link>
  </StaggerItem>
);

const Chip = ({ on, onClick, testid, children, small }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={on}
    data-testid={testid}
    className={`rounded-full border font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green focus-visible:ring-offset-2 ${
      small ? "px-4 py-2 text-xs" : "px-5 py-2.5 text-sm"
    } ${on ? "border-green bg-green text-white" : "border-[#E7EDF2] bg-white text-navy/70 hover:border-green hover:text-green"}`}
  >
    {children}
  </button>
);

const Prodotti = () => {
  const { CATALOG, PRODUCT_FILTERS, COPY } = useContent();
  const [searchParams, setSearchParams] = useSearchParams();

  const raw = searchParams.get("cat") || "tutti";
  const active = PRODUCT_FILTERS.some((f) => f.id === raw) ? raw : "tutti";

  const subList = active === "bendaggi"
    ? BENDAGGI_FAMILIES.map((f) => ({ id: f.id, label: f.label }))
    : SUBFILTERS[active] || [];
  const showSub = active !== "tutti" && subList.length > 0;

  const subRaw = searchParams.get("sub") || "tutte";
  const activeSub = showSub && subList.some((s) => s.id === subRaw) ? subRaw : "tutte";

  const filtered = useMemo(() => {
    let list = active === "tutti" ? CATALOG : CATALOG.filter((p) => (p.cats || []).includes(active));
    if (showSub && activeSub !== "tutte") {
      list = active === "bendaggi"
        ? list.filter((p) => p.family === activeSub)
        : list.filter((p) => (p.subs || []).includes(activeSub));
    }
    return list;
  }, [active, activeSub, showSub, CATALOG]);

  const selectFilter = (id) => {
    if (id === "tutti") setSearchParams({});
    else setSearchParams({ cat: id });
  };
  const selectSub = (id) => {
    if (id === "tutte") setSearchParams({ cat: active });
    else setSearchParams({ cat: active, sub: id });
  };
  const resetFilters = () => setSearchParams({});

  const activeL1Label = PRODUCT_FILTERS.find((f) => f.id === active)?.label;
  const activeSubLabel = subList.find((s) => s.id === activeSub)?.label;
  const hasActive = active !== "tutti";
  const subChips = [{ id: "tutte", label: `Tutte ${active === "preventive" ? "le preventive" : active === "bendaggi" ? "le bende" : "le voci"}` }, ...subList];

  return (
    <>
      <PageHero
        eyebrow="Prodotti"
        title={"Tutti i prodotti,\nin un unico catalogo."}
        subtitle="Filtra per soluzione e trova il prodotto giusto. Marchi e linee sono un dettaglio della scheda, non un criterio di scelta."
        testid="prodotti-hero"
      />

      <section className="py-16 lg:py-24 bg-white" data-testid="prodotti-catalog">
        <div className="container-x">
          {/* Livello 1 — soluzione */}
          <div className="flex flex-col gap-4">
            <span className="eyebrow">{COPY.ui.filtersLabel}</span>
            <div role="group" aria-label={COPY.ui.filtersLabel} className="flex flex-wrap gap-2.5" data-testid="catalog-filters">
              {PRODUCT_FILTERS.map((f) => (
                <Chip key={f.id} on={f.id === active} onClick={() => selectFilter(f.id)} testid={`catalog-filter-${f.id}`}>{f.label}</Chip>
              ))}
            </div>
          </div>

          {/* Livello 2 — filtri contestuali (solo dopo aver scelto una soluzione) */}
          {showSub && (
            <div className="mt-5 flex flex-col gap-3 rounded-2xl border border-[#E7EDF2] bg-cloud p-4" data-testid="catalog-subfilters">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-navy/50">Affina la ricerca</span>
              <div className="flex flex-wrap gap-2">
                {subChips.map((s) => (
                  <Chip key={s.id} small on={s.id === activeSub} onClick={() => selectSub(s.id)} testid={`catalog-sub-${s.id}`}>{s.label}</Chip>
                ))}
              </div>
            </div>
          )}

          {/* Filtri attivi + azzera */}
          {hasActive && (
            <div className="mt-5 flex flex-wrap items-center gap-2 text-sm" data-testid="catalog-active-filters">
              <span className="text-muted-foreground">Filtri attivi:</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-navy/5 px-3 py-1 font-medium text-navy">{activeL1Label}</span>
              {showSub && activeSub !== "tutte" && (
                <span className="inline-flex items-center gap-1 rounded-full bg-navy/5 px-3 py-1 font-medium text-navy">{activeSubLabel}</span>
              )}
              <button onClick={resetFilters} data-testid="catalog-reset" className="inline-flex items-center gap-1.5 rounded-full border border-[#E7EDF2] px-3 py-1 font-semibold text-navy/70 transition-colors hover:border-green hover:text-green">
                <RotateCcw size={13} /> Azzera filtri
              </button>
            </div>
          )}

          {/* Griglia */}
          {filtered.length > 0 ? (
            <div className="mt-12" data-testid="catalog-grid">
              <Stagger key={`${active}-${activeSub}`} className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((p) => (
                  <ProductCard key={p.id} p={p} photoLabel={COPY.ui.photoSoon} brandLabel={COPY.ui.brandLabel} cta={COPY.ui.productDiscover} />
                ))}
              </Stagger>
            </div>
          ) : (
            <div className="mt-12 flex flex-col items-start gap-3" data-testid="catalog-empty">
              <p className="text-muted-foreground">Nessun prodotto corrisponde ai filtri selezionati.</p>
              <button onClick={resetFilters} className="inline-flex items-center gap-1.5 text-sm font-semibold text-green">
                <X size={15} /> Azzera i filtri
              </button>
            </div>
          )}
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default Prodotti;
